/* cwclientlibjs
   copyright 2015 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import 'isomorphic-fetch';
import * as CryptoJS from 'crypto-js';

import {
    Schema,
    EntitySchema,
    getFromCardinality,
    getToCardinality,
    EntityAttributeSchema,
    EntityRelationSchema,
} from './providers';

function getCookieValueFromArray(cookies: string[], name: string) {
    for (const cookie of cookies) {
        if (cookie.startsWith(name + '=')) {
            return decodeURIComponent(cookie.substring(name.length + 1));
        }
    }
    return null;
}

function getCookieValueFromString(cookiesString: string, name: string) {
    const cookies = cookiesString.split(';').map((cookie) => cookie.trim());
    return getCookieValueFromArray(cookies, name);
}

function getCookie(name: string) {
    // When runnnig in node, document is not defined and we can't use cookies
    if (
        typeof document !== 'undefined' &&
        document.cookie &&
        document.cookie !== ''
    ) {
        return getCookieValueFromString(document.cookie, name);
    }
    return null;
}

export function applySchema(jsonEntity: any): EntitySchema {
    const adaptAttribute = (attr: any): EntityAttributeSchema => {
        return {...attr, cardinality: getToCardinality(attr.cardinality)};
    };
    const adaptRelation = (relation: any): EntityRelationSchema => {
        return {
            ...relation,
            fromCardinality: getFromCardinality(relation.fromCardinality),
            toCardinality: getToCardinality(relation.toCardinality),
        };
    };
    return {
        ...jsonEntity,
        attributes: jsonEntity.attributes.map(adaptAttribute),
        relationsTo: jsonEntity.relationsTo.map(adaptRelation),
        relationsFrom: jsonEntity.relationsFrom.map(adaptRelation),
    };
}

/**
 * Gets the passed URL ensuring it ends with a slash
 * @param url Th input URL
 */
function endUrlWithSlash(url: string): string {
    if (url[url.length - 1] !== '/') {
        url += '/';
    }
    return url;
}

/**
 * Interface for an http response
 */
export interface HttpResponse {
    /**
     * The HTTP response code
     */
    code: number;
    /**
     * The content of the response
     */
    content: any;
}

/**
 * Executes an HTTP request using the Fetch API
 * @param uri The URI
 * @param method The method to use
 * @param content The content, if any
 * @param headers The request headers
 * @param allowsCrossOrigin Whether to allow cross-origin requests
 */
function doRequestFetch(
    uri: string,
    method: string = 'GET',
    content: any = null,
    headers: Headers,
    onResponseHeaders: (headers: Headers) => void,
    allowsCrossOrigin: boolean = false,
    csrfToken: string | null = null
): Promise<HttpResponse> {
    if (!uri.startsWith('http')) {
        uri = 'http://' + uri;
    }
    if (method !== 'GET' && !allowsCrossOrigin) {
        if (csrfToken === null) {
            csrfToken = getCookie('csrf_token');
        }
        if (csrfToken !== null) {
            headers.append('X-CSRF-Token', csrfToken);
        }
    }
    return fetch(uri, {
        method: method,
        body: content,
        headers: headers,
        redirect: 'manual',
        credentials: allowsCrossOrigin ? 'include' : 'same-origin',
    }).then((response) => {
        onResponseHeaders(response.headers);
        let contentType = response.headers.get('Content-Type');
        let toHttpResponse = (content: any): HttpResponse => {
            let myResponse = {
                code: response.status,
                content: content,
            };
            if (
                (response.status === 0 && response.type === 'opaqueredirect') ||
                (response.status >= 200 && response.status < 400)
            ) {
                return myResponse;
            } else {
                if (myResponse.content === '') {
                    myResponse = {
                        code: response.status,
                        content: response.statusText,
                    };
                }
                throw myResponse;
            }
        };
        if (contentType === 'application/json') {
            return response.json().then(toHttpResponse);
        } else {
            return response.text().then(toHttpResponse);
        }
    });
}

/**
 * Gets the final content to be sent
 * @param content The content, if any
 * @param contentType The contentType, if any
 */
function getFinalContent(
    content: any = null,
    contentType: string = 'application/json'
): string | null {
    let finalContent = content;
    if (content !== null && contentType === 'application/json') {
        finalContent = JSON.stringify(content);
    }
    return finalContent;
}

/**
 * Executes an HTTP request using the Fetch API
 * @param uri The URI
 * @param method The method to use
 * @param content The content, if any
 * @param contentType The contentType, if any
 * @param accept The HTTP Accept header, if any
 * @param cookieJar The cookies
 * @param allowsCrossOrigin Whether to allow cross-origin requests
 */
function requestFetchWithCookies(
    uri: string,
    method: string = 'GET',
    content: any = null,
    contentType: string | null = 'application/json',
    accept: string = 'application/json',
    cookieJar: string[],
    allowsCrossOrigin: boolean = false
): Promise<HttpResponse> {
    let requestHeaders = new Headers();
    let finalContent = content;
    if (contentType !== null) {
        finalContent = getFinalContent(content, contentType);
        requestHeaders.append('Content-Type', contentType);
    }
    requestHeaders.append('Accept', accept);
    cookieJar.forEach((cookie: string) => {
        requestHeaders.append('Cookie', cookie);
    });
    return doRequestFetch(
        uri,
        method,
        finalContent,
        requestHeaders,
        (responseHeaders) => {
            const cookiesString = responseHeaders.get('Set-Cookie');
            if (cookiesString !== null) {
                const cookies = cookiesString.split(',');
                for (let cookie of cookies) {
                    const index = cookie.indexOf(';');
                    if (index > 0) {
                        cookie = cookie.substring(0, index).trim();
                    }
                    cookieJar.push(cookie);
                }
            }
        },
        allowsCrossOrigin,
        getCookieValueFromArray(cookieJar, 'csrf_token')
    );
}

/**
 * The hash method to use when signing a request
 */
export enum SignedContentHashMethod {
    /**
     * MD5, legacy for RQL/IO version 1.0
     */
    MD5,
    /**
     * SHA2-512, for RQL/IO version 2.0
     */
    SHA512,
}

/**
 * Gets the CryptoJS hash function for the selected hash method
 * @param hashMethod The hash method to use when signing a request
 * @returns The appropriate hash function
 */
function getHashFunctionFor(
    hashMethod: SignedContentHashMethod
): (input: string) => CryptoJS.WordArray {
    switch (hashMethod) {
        case SignedContentHashMethod.MD5:
            return CryptoJS.MD5;
        case SignedContentHashMethod.SHA512:
            return CryptoJS.SHA512;
    }
}

/**
 * Gets the CryptoJS hmac for the selected hash method
 * @param hashMethod The hash method to use when signing a request
 * @returns The appropriate hmac function
 */
function getHmacFunctionFor(
    hashMethod: SignedContentHashMethod
): (contentToSign: string, tokenValue: string) => CryptoJS.WordArray {
    switch (hashMethod) {
        case SignedContentHashMethod.MD5:
            return CryptoJS.HmacMD5;
        case SignedContentHashMethod.SHA512:
            return CryptoJS.HmacSHA512;
    }
}

/**
 * Gets the name of the HTTP header for the content hash related to the selected hash method
 * @param hashMethod The hash method to use when signing a request
 * @returns The name of the appropriate HTTP header
 */
function getContentHashHeaderFor(hashMethod: SignedContentHashMethod): string {
    switch (hashMethod) {
        case SignedContentHashMethod.MD5:
            return 'Content-MD5';
        case SignedContentHashMethod.SHA512:
            return 'Content-SHA512';
    }
}

/**
 * Executes a signed HTTP request using the Fetch API
 * @param uri The URI
 * @param method The method to use
 * @param content The content, if any
 * @param contentType The contentType, if any
 * @param accept The HTTP Accept header, if any
 * @param hashMethod The hash method to use when signing a request
 * @param tokenName The name of the token to use
 * @param tokenValue The token to use for signing
 */
function requestSignAndFetch(
    uri: string,
    method: string = 'GET',
    content: any = null,
    contentType: string = 'application/json',
    accept: string = 'application/json',
    hashMethod: SignedContentHashMethod,
    tokenName: string,
    tokenValue: string
): Promise<HttpResponse> {
    let finalContent = getFinalContent(content, contentType);
    const date = new Date(Date.now()).toUTCString();
    const contentHash = getHashFunctionFor(hashMethod)(
        finalContent === null ? '' : finalContent
    ).toString(CryptoJS.enc.Hex);
    const contentToSign = method + uri + contentHash + contentType + date;
    const hmac = getHmacFunctionFor(hashMethod)(
        contentToSign,
        tokenValue
    ).toString(CryptoJS.enc.Hex);
    let requestHeaders = new Headers();
    requestHeaders.append('Accept', accept);
    requestHeaders.append('Content-Type', contentType);
    requestHeaders.append(getContentHashHeaderFor(hashMethod), contentHash);
    requestHeaders.append('Date', date);
    requestHeaders.append('X-Cubicweb-Date', date);
    requestHeaders.append('Authorization', `Cubicweb ${tokenName}:${hmac}`);
    // tslint:disable-next-line:no-empty
    return doRequestFetch(
        uri,
        method,
        finalContent,
        requestHeaders,
        (_) => {},
        false
    );
}

/**
 * An HTTP client for a cubicweb instance
 */
export interface HttpClient {
    /**
     * The base URL to the CubicWeb instance
     */
    readonly baseUrl: string;
    /**
     * Executes an HTTP request
     * @param path The path and query string
     * @param method The method to use
     * @param content The content, if any
     * @param contentType The contentType, if any
     * @param accept The HTTP Accept header, if any
     */
    request(
        path: string,
        method: string,
        content: any,
        contentType: string | null,
        accept: string
    ): Promise<HttpResponse>;
}

/**
 * Basic implementation of an HTTP client for cubicweb
 */
export class CwSimpleHttpClient implements HttpClient {
    /**
     * The base URL to the CubicWeb instance
     */
    public readonly baseUrl: string;
    /**
     * Whether to allow cross-origin requests
     */
    public readonly allowsCrossOrigin: boolean;
    /**
     * The current cookies
     */
    private cookieJar: string[];
    /**
     * The current login
     */
    private login: string | null;

    /**
     * Initializes this proxy
     * @param baseUrl The base URL to the CubicWeb instance
     * @param allowsCrossOrigin Whether to allow cross-origin requests
     */
    constructor(baseUrl: string, allowsCrossOrigin: boolean = false) {
        this.baseUrl = endUrlWithSlash(baseUrl);
        this.allowsCrossOrigin = allowsCrossOrigin;
        this.cookieJar = [];
        this.login = null;
    }

    /**
     * Returns whether this client has cookies
     */
    public hasCookies(): boolean {
        return this.cookieJar.length > 0;
    }

    public request(
        path: string,
        method: string,
        content: any,
        contentType: string | null,
        accept: string
    ): Promise<HttpResponse> {
        return requestFetchWithCookies(
            this.baseUrl + path,
            method,
            content,
            contentType,
            accept,
            this.cookieJar,
            this.allowsCrossOrigin
        ).then((response) => {
            if (
                response.content !== null &&
                typeof response.content === 'object' &&
                Object.getOwnPropertyNames(response.content).indexOf(
                    'reason'
                ) >= 0
            ) {
                // this is an error
                throw response;
            }
            return response;
        });
    }

    /**
     * Performs login onto the associated cubicweb instance
     * @param login The user login
     * @param password The password
     */
    public doLogin(login: string, password: string): Promise<HttpResponse> {
        let content =
            `__form_id=logform` +
            `&__domid=loginForm` +
            `&_cw_fields=__password%2C__login` +
            `&__login=${encodeURIComponent(login)}` +
            `&__password=${encodeURIComponent(password)}`;
        // initial request to try to get a session cookie
        return this.request(
            'login',
            'GET',
            null,
            'application/x-www-form-urlencoded',
            'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
        ).then((_) => {
            // try to authenticate
            return this.request(
                'login',
                'POST',
                content,
                'application/x-www-form-urlencoded',
                'application/json'
            ).then((response) => {
                this.login = login;
                return response;
            });
        });
    }

    public toSigningClient(): Promise<CwSigningHttpClient> {
        if (this.login === null) {
            return new Promise((_, reject) => reject('no login'));
        }
        const rqlClient = new CwRqlClient(this);
        return rqlClient
            .queryAndTransform(
                'Any I,T WHERE X token_for_user U, X id I, X token T, X enabled TRUE, U login %(login)s',
                {login: this.login},
                'csvexport'
            )
            .then((csv: string) => {
                const lines = csv.split('\n');
                if (lines.length <= 1 || lines[1] === '') {
                    throw new Error('No data');
                }
                const [tokenId, tokenSecret] = lines[1].split(',');
                return new CwSigningHttpClient(
                    this.baseUrl,
                    tokenId,
                    tokenSecret
                );
            });
    }
}

/**
 * Implementation of an HTTP client for cubicweb that use request signing
 */
export class CwSigningHttpClient implements HttpClient {
    /**
     * The base URL to the CubicWeb instance
     */
    public readonly baseUrl: string;
    /**
     * The name of the token to use
     */
    private readonly tokenName: string;
    /**
     * The token to use for signing
     */
    private readonly tokenValue: string;
    /**
     * The hash method to use when signing a request
     */
    private readonly hashMethod: SignedContentHashMethod;

    /**
     * Initializes this proxy
     * @param baseUrl The base URL to the CubicWeb instance
     * @param tokenName The name of the token to use
     * @param tokenValue The token to use for signing
     * @param hashMethod The hash method to use when signing a request
     */
    constructor(
        baseUrl: string,
        tokenName: string,
        tokenValue: string,
        hashMethod: SignedContentHashMethod = SignedContentHashMethod.SHA512
    ) {
        this.baseUrl = endUrlWithSlash(baseUrl);
        this.tokenName = tokenName;
        this.tokenValue = tokenValue;
        this.hashMethod = hashMethod;
    }

    public request(
        path: string,
        method: string,
        content: any,
        contentType: string,
        accept: string
    ): Promise<HttpResponse> {
        return requestSignAndFetch(
            this.baseUrl + path,
            method,
            content,
            contentType,
            accept,
            this.hashMethod,
            this.tokenName,
            this.tokenValue
        ).then((response) => {
            if (
                response.content !== null &&
                typeof response.content === 'object' &&
                Object.getOwnPropertyNames(response.content).indexOf(
                    'reason'
                ) >= 0
            ) {
                // this is an error
                throw response;
            }
            return response;
        });
    }
}

/**
 * Interpolates the specified RQL query with a dictionary of parameters
 * @param str The base RQL query to interpolate
 * @param dict The dictionary of parameters
 */
export function interpolateRql(
    str: string,
    dict: {[key: string]: any}
): string {
    return str.replace(/%\(([a-zA-Z0-9]+?)\)s/g, function (base, pattern) {
        return pattern in dict ? JSON.stringify(dict[pattern]) : base;
    });
}

/**
 * A row in a RQL resut set
 */
export type RqlRow = any[];

/**
 * A sets of rows in a RQL result set
 */
export type RqlRows = RqlRow[];

/**
 * A response to a RQL/IO v2.0 single query
 */
export type RqlIoV1QueryResponse = RqlRows;

/**
 * A response to a RQL/IO v2.0 transaction
 */
export type RqlIoV1TransactionResponse = RqlIoV1QueryResponse[];

/**
 * A response to a RQL/IO v2.0 single query
 */
export type RqlIoV2QueryResponse = {
    rows: RqlRows;
    variables: string[];
};

/**
 * A response to a RQL/IO v2.0 transaction
 */
export type RqlIoV2TransactionResponse = RqlIoV2QueryResponse[];

/**
 * The bindings representation for a single response (equivalent to a row) to a RQL select query
 */
export type RqlSolution = {[key: string]: any};

/**
 * The bindings representation for the response to a RQL select query
 */
export type RqlSolutions = RqlSolution[];

/**
 * A RQL query to be executed in a transaction as a RQL query string and its associated arguments
 */
export type RqlQuery = [string, {[key: string]: any}];

/**
 * A RQL transaction a an ordered list of RQL queries
 */
export type RqlTransaction = RqlQuery[];

/**
 * Builds a symbolic solution to a RQL select query
 * @param row The row representing the solution
 * @param variables The ordered variables
 * @returns The solution
 */
export function rowAsSolution(row: RqlRow, variables: string[]): RqlSolution {
    if (row.length > variables.length) {
        throw new Error(
            `cannot produce bindings from a row of length ${row.length} using ${variables.length} variables`
        );
    }
    const solution: RqlSolution = {};
    for (let i = 0; i !== row.length; i++) {
        solution[variables[i]] = row[i];
    }
    return solution;
}

/**
 * Build symbolic solutions from a response to a query
 * @param rows The rows representing the solutions
 * @param variables The ordered variables
 * @returns The solutions
 */
export function rowsAsSolutions(
    rows: RqlRows,
    variables: string[]
): RqlSolutions {
    return rows.map((row) => rowAsSolution(row, variables));
}

/**
 * Build symbolic solutions from a response to a query
 * @param response A response to a RQL select query in the RQL/IO v2 protocol
 * @returns The solutions
 */
export function rqlIoV2asSolutions(
    response: RqlIoV2QueryResponse
): RqlSolutions {
    return rowsAsSolutions(response.rows, response.variables);
}

/**
 * A client able to execute RQL queries
 */
export interface RqlClient {
    /**
     * Executes a RQL query
     * @param query The RQL query
     * @param params The dictionary of query parameters
     */
    queryRows(query: string, params?: {[key: string]: any}): Promise<RqlRows>;

    /**
     * Executes a RQL query and gets the results as bindings
     * @param query The RQL query
     * @param params The dictionary of query parameters
     */
    queryBindings(
        query: string,
        params?: {[key: string]: any}
    ): Promise<RqlSolutions>;

    /**
     * Executes a RQL query, get entities in col param and gets the results as an object
     * @param query The RQL query
     * @param col the column where are eid of entities in the result set
     * @param params The dictionary of query parameters
     */
    getEntities(
        query: string,
        col: number,
        params?: {[key: string]: any}
    ): Promise<RqlSolutions>;

    /**
     * Executes a RQL query
     * @param query The RQL query
     * @param params The dictionary of query parameters
     * @param viewId The identifier of the view to use
     */
    queryAndTransform(
        query: string,
        params: {[key: string]: any},
        viewId: string
    ): Promise<any>;

    /**
     * Fetch the cubicweb schema from a server supporting cubicweb_rqlcontroller
     */
    getSchema(): Promise<Schema>;
    /**
     * Fetch the cubicweb schema hash from a server supporting cubicweb_rqlcontroller
     */
    getSchemaHash(): Promise<string | null>;
    /**
     * Executes RQL queries in a transaction through RQL/IO v1.0
     * @param queries The queries
     */
    transactionV1(queries: RqlTransaction): Promise<RqlIoV1TransactionResponse>;

    /**
     * Executes RQL queries in a transaction through RQL/IO v2.0
     * @param queries The queries
     */
    transactionV2(queries: RqlTransaction): Promise<RqlIoV2TransactionResponse>;
}

/**
 * A proxy to a CubicWeb instance
 */
export class CwRqlClient implements RqlClient {
    /**
     * The underlying HTTP client
     */
    public readonly httpClient: HttpClient;
    /**
     * The identifier of the default view to use
     */
    private readonly defaultVid: string;

    /**
     * Initializes this proxy
     * @param httpClient The underlying HTTP client
     */
    constructor(httpClient: HttpClient) {
        this.httpClient = httpClient;
        this.defaultVid = 'jsonexport';
    }

    public queryRows(
        query: string,
        params?: {[key: string]: any}
    ): Promise<RqlRows> {
        const finalParams = typeof params === 'undefined' ? {} : params;
        return this.transactionV1([[query, finalParams]]).then((results) => {
            return results[0];
        });
    }

    public queryBindings(
        query: string,
        params?: {[key: string]: any}
    ): Promise<RqlSolutions> {
        const finalParams = typeof params === 'undefined' ? {} : params;
        return this.transactionV2([[query, finalParams]]).then((results) => {
            return rqlIoV2asSolutions(results[0]);
        });
    }

    public getEntities(
        query: string,
        col: number = 0,
        params?: {[key: string]: any}
    ): Promise<RqlSolutions> {
        const finalParams = params ?? {};
        return this.transactionGetEntities([[query, finalParams]], col);
    }

    public getSchema(): Promise<Schema> {
        return this.httpClient
            .request('rqlio/schema/', 'GET', null, null, 'application/json')
            .then((response) => {
                return {
                    metadata: {
                        timestamp: new Date(),
                    },
                    entities: response.content['entities'].map(applySchema),
                };
            });
    }

    public getSchemaHash(): Promise<string | null> {
        return fetch(`${this.httpClient.baseUrl}rqlio/schema/`, {
            method: 'HEAD',
        }).then((response) => {
            const hash = response.headers.get('Etag');
            return hash ? hash : null;
        });
    }

    public queryAndTransform(
        query: string,
        params: {[key: string]: any},
        viewId: string
    ): Promise<any> {
        return this.httpClient
            .request(
                this.buildUrl(query, params, viewId),
                'GET',
                null,
                'application/json',
                'application/json'
            )
            .then((response) => response.content);
    }

    public transactionV1(
        queries: RqlTransaction
    ): Promise<RqlIoV1TransactionResponse> {
        return this.httpClient
            .request(
                'rqlio/1.0/',
                'POST',
                queries,
                'application/json',
                'application/json'
            )
            .then((response) => {
                return response.content;
            })
            .catch((error) => {
                if (error instanceof Error) {
                    throw error;
                } else {
                    let errorReason = error.content;
                    try {
                        let errorContent = JSON.parse(error.content);
                        if (errorContent.reason !== undefined) {
                            errorReason = errorContent.reason;
                        }
                    } finally {
                        throw errorReason;
                    }
                }
            });
    }

    public transactionV2(
        queries: RqlTransaction
    ): Promise<RqlIoV2TransactionResponse> {
        return this.httpClient
            .request(
                'rqlio/2.0/',
                'POST',
                queries,
                'application/json',
                'application/json'
            )
            .then((response) => {
                return response.content;
            });
    }

    public transactionGetEntities(
        queries: RqlTransaction,
        col: number = 0
    ): Promise<RqlSolutions> {
        return this.httpClient
            .request(
                'rqlio/get_entities/',
                'POST',
                [queries, col],
                'application/json',
                'application/json'
            )
            .then((response) => {
                return response.content;
            });
    }

    /**
     * Gets the URL to query
     * @param query The RQL query
     * @param params The dictionary of query parameters
     * @param vid The identifier of the view to use (or undefined if the default view shall be used)
     */
    public buildUrl(
        query: string,
        params: {[key: string]: any},
        vid: string | undefined = undefined
    ): string {
        if (vid === undefined) {
            vid = this.defaultVid;
        }
        return 'view?rql=' + interpolateRql(query, params) + '&vid=' + vid;
    }
}
